import type { PrintFormat } from '@/types';
import { paperDimensions, MM_TO_PX } from '@/shared/utils/paperSizes';

/** Canonical JSON shape used for copy/download/upload of bill templates. */
export interface BillTemplateJson {
  contentBlocks: unknown[];
  editorConfig: Record<string, unknown>;
}

export function buildBillTemplateJson(
  printFormat: PrintFormat,
  existingEditorConfig?: Record<string, unknown>,
  zoom?: number,
): BillTemplateJson {
  const dims = paperDimensions(
    printFormat.paper.size,
    printFormat.paper.orientation,
    printFormat.paper.customWidthMm,
    printFormat.paper.customHeightMm,
  );

  const editorConfig: Record<string, unknown> = {
    ...(existingEditorConfig ?? {}),
    formatType: 'printFormat',
    printFormat,
    page_size: printFormat.paper.size,
    orientation:
      printFormat.paper.orientation === 'portrait' ? 'Portrait' : 'Landscape',
    width: Math.round(dims.width * MM_TO_PX),
    height: Math.round(dims.height * MM_TO_PX),
    no_of_item: printFormat.paper.itemsPerPage ?? 0,
    default_font_family: printFormat.defaultFontFamily ?? '',
    zoom: zoom ?? (existingEditorConfig?.zoom as number | undefined) ?? 1,
    margin: {
      top: printFormat.paper.margins.top,
      right: printFormat.paper.margins.right,
      bottom: printFormat.paper.margins.bottom,
      left: printFormat.paper.margins.left,
    },
  };

  return {
    contentBlocks: [],
    editorConfig,
  };
}

export function parseBillTemplateJson(
  raw: string | BillTemplateJson | null | undefined,
): BillTemplateJson | null {
  if (!raw) return null;
  try {
    const parsed =
      typeof raw === 'string' ? (JSON.parse(raw) as BillTemplateJson) : raw;
    if (parsed?.editorConfig) return parsed;
    return null;
  } catch {
    return null;
  }
}

export function getPrintFormatFromTemplate(
  editorConfig?: Record<string, unknown>,
  configuration?: string | BillTemplateJson,
): PrintFormat | null {
  const fromEditor = editorConfig?.printFormat as PrintFormat | undefined;
  if (fromEditor?.paper && Array.isArray(fromEditor.elements)) return fromEditor;

  const parsed = parseBillTemplateJson(configuration);
  const fromConfig = parsed?.editorConfig?.printFormat as PrintFormat | undefined;
  if (fromConfig?.paper && Array.isArray(fromConfig.elements)) return fromConfig;

  return fromEditor ?? fromConfig ?? null;
}

export interface BillSetupApiPayload {
  name: string;
  voucher_type: unknown;
  transaction_type: unknown;
  transactionType?: string;
  branchId?: string;
  companyId?: string;
  content_blocks: unknown[];
  contentBlocks: unknown[];
  editor_config: Record<string, unknown>;
  editorConfig: Record<string, unknown>;
  /** Full template as a JSON string for the backend */
  configuration: string;
}

export function buildBillSetupApiPayload(
  printFormat: PrintFormat,
  meta: {
    name: string;
    type: unknown;
    branchId?: string;
    companyId?: string;
    transactionType?: string;
  },
  existingEditorConfig?: Record<string, unknown>,
  zoom?: number,
): BillSetupApiPayload {
  const templateJson = buildBillTemplateJson(printFormat, existingEditorConfig, zoom);

  return {
    name: meta.name,
    voucher_type: meta.type,
    transaction_type: meta.type,
    transactionType: meta.transactionType ?? (meta.type as string | undefined),
    branchId: meta.branchId,
    companyId: meta.companyId,
    content_blocks: templateJson.contentBlocks,
    contentBlocks: templateJson.contentBlocks,
    editor_config: templateJson.editorConfig,
    editorConfig: templateJson.editorConfig,
    configuration: JSON.stringify(templateJson),
  };
}
